import React, { useEffect, useState, useCallback } from 'react';
import {
  Stack,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Box,
  TextField,
  InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';
import {
  fetchApprovals,
  cancelApproval,
  type DocumentApproval,
} from '../services/documentApprovalApi';
import { CreateApprovalDialog } from '../components/document-approvals/CreateApprovalDialog';
import { ApprovalDetailDialog } from '../components/document-approvals/ApprovalDetailDialog';

const STATUS_FILTERS = ['all', 'pending', 'approved', 'rejected', 'expired'] as const;

const statusColor = (s: string) => {
  switch (s) {
    case 'pending':
      return 'warning';
    case 'approved':
      return 'success';
    case 'rejected':
      return 'error';
    default:
      return 'default';
  }
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('nl-NL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function getWorkerName(doc: DocumentApproval): string {
  const w = doc.worker;
  if (!w) return doc.workerId.slice(0, 8) + '...';
  return [w.firstName, w.prefix, w.lastName].filter(Boolean).join(' ');
}

const DocumentApprovalsPage: React.FC = () => {
  const { accessToken } = useSelector((state: RootState) => (state as any).auth);

  const [approvals, setApprovals] = useState<DocumentApproval[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchText, setSearchText] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [detailApproval, setDetailApproval] = useState<DocumentApproval | null>(null);

  const loadApprovals = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const filters: Record<string, string> = {};
      if (statusFilter !== 'all') filters.status = statusFilter;
      const data = await fetchApprovals(accessToken, filters);
      setApprovals(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to load approvals');
    } finally {
      setLoading(false);
    }
  }, [accessToken, statusFilter]);

  useEffect(() => {
    loadApprovals();
  }, [loadApprovals]);

  const handleCancelApproval = async (id: string) => {
    try {
      await cancelApproval(accessToken, id);
      loadApprovals();
    } catch (err: any) {
      setError(err?.message || 'Failed to cancel');
    }
  };

  const filtered = searchText
    ? approvals.filter((a) => {
        const name = getWorkerName(a).toLowerCase();
        const t = a.title.toLowerCase();
        const q = searchText.toLowerCase();
        return name.includes(q) || t.includes(q);
      })
    : approvals;

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h5" fontWeight={800}>
          Document Approvals
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          New Approval
        </Button>
      </Stack>

      {/* Filters */}
      <Card variant="outlined">
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {STATUS_FILTERS.map((s) => (
              <Chip
                key={s}
                label={s.charAt(0).toUpperCase() + s.slice(1)}
                variant={statusFilter === s ? 'filled' : 'outlined'}
                color={s === 'all' ? 'default' : (statusColor(s) as any)}
                onClick={() => setStatusFilter(s)}
                size="small"
              />
            ))}
            <Box sx={{ flex: 1 }} />
            <TextField
              size="small"
              placeholder="Search worker or title..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{ minWidth: 240 }}
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Table */}
      {error && <Alert severity="error">{error}</Alert>}

      <Card variant="outlined">
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {approvals.length === 0
                ? 'No document approvals yet. Create one to get started.'
                : 'No results match your filters.'}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Worker</TableCell>
                  <TableCell>Document</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((a) => (
                  <TableRow key={a.id} hover>
                    <TableCell>
                      <Typography variant="body2">{getWorkerName(a)}</Typography>
                      {a.worker?.email && (
                        <Typography variant="caption" color="text.secondary">
                          {a.worker.email}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 240 }}>
                        {a.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {a.documentType.replace(/_/g, ' ')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={a.status}
                        color={statusColor(a.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(a.createdAt)}</TableCell>
                    <TableCell>{formatDate(a.expiresAt)}</TableCell>
                    <TableCell align="right">
                      <Tooltip
                        title="View details"
                        children={
                          <IconButton size="small" onClick={() => setDetailApproval(a)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        }
                      />
                      {a.status === 'pending' && (
                        <Tooltip
                          title="Cancel"
                          children={
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleCancelApproval(a.id)}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          }
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Dialogs */}
      <CreateApprovalDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={loadApprovals}
        accessToken={accessToken}
      />

      <ApprovalDetailDialog
        open={!!detailApproval}
        onClose={() => setDetailApproval(null)}
        approval={detailApproval}
        accessToken={accessToken}
        onCancelled={loadApprovals}
      />
    </Stack>
  );
};

export default DocumentApprovalsPage;
